import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { OPTIMISTIC_ID_PREFIX } from 'src/graphql/entities/custom-view';
import {
  GET_PLATFORM_USERS_QUERY,
  getCurrentUserQueryVariables,
  GetPlatformUserQuery,
  useCurrentUserProfile,
} from 'src/graphql/entities/platform-user';
import { CREATE_USER_SETTING_VALUE_QUERY } from 'src/graphql/entities/user-setting-value/user-setting-value.operations';
import { UserSettingValue } from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import { QueryVariables } from 'src/graphql/types';
import { UserSettingValueRaw } from 'src/types';

export interface CreateUserSettingValue {
  createUserSettingValue: UserSettingValue[];
}

export const useCreateUserSettingValue = () => {
  const { user } = useCurrentUserProfile();

  const [createUserSettingValue, { loading: isLoading }] = useMutation<CreateUserSettingValue>(
    CREATE_USER_SETTING_VALUE_QUERY,
    {
      update(cache, { data }) {
        if (!data?.createUserSettingValue?.length) return;

        cache.updateQuery<GetPlatformUserQuery, QueryVariables>(
          {
            query: GET_PLATFORM_USERS_QUERY,
            variables: getCurrentUserQueryVariables(),
          },
          (existingData) => {
            if (!existingData?.getPlatformUser?.records?.length) return existingData;

            const newData = structuredClone(existingData);
            const user = newData.getPlatformUser.records[0];

            user.userSettingValues.push(
              data?.createUserSettingValue[0] as UserSettingValueRaw,
            );

            return newData;
          },
        );
      },
    },
  );

  const createWithOptimisticResponse = useCallback(
    (
      data: { userSettingId: string; value: string; valueType: string },
      onCreated?: (row: UserSettingValue) => void,
    ) => {
      const update = { ...data, userId: user!.userId, partnerId: user!.partnerId };

      void createUserSettingValue({
        variables: { data: update },
        optimisticResponse() {
          const nowString = new Date().toISOString();
          const tempId = `${OPTIMISTIC_ID_PREFIX}${nowString}`;

          return {
            createUserSettingValue: [
              {
                __typename: 'UserSettingValue',
                ...update,
                id: tempId,
                createdAt: nowString,
                updatedAt: nowString,
              },
            ],
          };
        },
        onCompleted(result) {
          const created = result?.createUserSettingValue?.[0];

          if (created) {
            onCreated?.(created);
          }
        },
      });
    },
    [createUserSettingValue, user],
  );

  return {
    createUserSettingValue: createWithOptimisticResponse,
    isLoading,
  };
};
